import { Api, CompanySettings, LicenseTester, ModelRow, Navigation } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    Button, ChangeEvent, ClickEvent, DataSourceExecutionEvent, Image, Label, Layout, Panel, Tab, TreeNode
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutResponsibilityControl } from "./autogen/AutogenLayoutResponsibilityControl";
import { HierarchyItem } from "./ResponsibilityHierarchyModel";
import { ResponsibilityManageHierarchy } from "./ResponsibilityManageHierarchy";
import { ResponsibilityManageHierarchyLevel } from "./ResponsibilityManageHierarchyLevel";
import { ResponsibilityUtil } from "./ResponsibilityUtil";

export class ResponsibilityControl extends AutogenLayoutResponsibilityControl {

    private layoutManageHierarchyLevel = Layout.getLayout("lme/general/ResponsibilityManageHierarchyLevel") as ResponsibilityManageHierarchyLevel;
    private layoutManageHierarchy = Layout.getLayout("lme/general/ResponsibilityManageHierarchy") as ResponsibilityManageHierarchy;

    private _isHierarchyExist: boolean = false;
    private _isHierarchyLevelExist: boolean = false;
    private _hierarchyLevelCount: number = 0;
    private _hierarchyCount: number = 0;
    public get isHierarchyExist(): boolean {return this._isHierarchyExist; }
    public set isHierarchyExist(value: boolean) {this._isHierarchyExist = value; }
    public get isHierarchyLevelExist(): boolean {return this._isHierarchyLevelExist; }
    public set isHierarchyLevelExist(value: boolean) {this._isHierarchyLevelExist = value; }
    public get hierarchyLevelCount(): number { return this._hierarchyLevelCount; }
    public set hierarchyLevelCount(value: number) { this._hierarchyLevelCount = value; }
    public get hierarchyCount(): number { return this._hierarchyCount; }
    public set hierarchyCount(value: number) { this._hierarchyCount = value; }

    override onLoad() {
        const isCompanyTypeAsset: boolean =  CompanySettings.get().is_asset;
        const isCommisionEnabled: boolean =  CompanySettings.get().commission_enabled;
        this.tabDataFusion.visible = this.isDataFusionLicensed();
        this.tabCommissions.visible = isCommisionEnabled;
        this.tabDrivers.visible = isCompanyTypeAsset;
        this.tabTractors.visible = isCompanyTypeAsset;
        this.tabTrailers.visible = isCompanyTypeAsset;
        this.textboxRespAssetDispatchResponsibility.visible = isCompanyTypeAsset;
        const tabInfoMappings: { tab: Tab; text: string }[] = [
            { tab: this.tabBilling, text: "Filtering will follow the parameters set up for Orders." },
            { tab: this.tabSettlement, text: "Filtering based on the Movement record." },
            { tab: this.tabOSD, text: "Filtering will follow the parameters set up for Orders." },
        ];

        tabInfoMappings.forEach(({ tab, text }) => {
            this.addInfoPanelToTab(tab, text);
        });
    }

    isDataFusionLicensed(): boolean {
        return LicenseTester.testPackage("com.tms.client.loadmaster.edi");
    }

    private addInfoPanelToTab(tab: Tab, labelText: string): void {
        const infoPanel = new Panel({ id: "panelInfo", padding: 0, margin: 0, rowBreak: false });
        const image = new Image({ id: "imageInfo", name: ImageName.info, color: "primary", rowBreak: false, marginLeft: 8 });
        const label = new Label({ id: "labelInfo", text: labelText, color: "default.lightest" });
        infoPanel.add(image, label);
        tab.add(infoPanel);
    }

    switchAutoAssignOrderMovementOnChange(event: ChangeEvent) {
        const isToggle = this.switchAutoAssignOrderMovement.checked;
        this.textboxRespOrderAssign.enabled = isToggle;
    }

    switchAutoDispatchAutoAssignmentOnChange(event: ChangeEvent) {
        const isToggle = this.switchAutoDispatchAutoAssignment.checked;
        this.textboxRespAssetDispatchResponsibility.enabled = isToggle;
        this.textboxRespBrokerageDispatchResponsibility.enabled = isToggle;
    }

    async sourceAgentAccessCtrlAfterExecution(event: DataSourceExecutionEvent) {
        await this.refreshHierarchyStatus();
        await this.populateTree();
    }

    /**
     * Refreshes the hierarchy existence flags and header button caption. Call after add/update/delete.
     */
    async refreshHierarchyStatus() {
        const response = await Api.search("lme/general/validate-hierarchy", { "operation": "Both" });
        const data = response?.data[0];
        if (data) {
            this.isHierarchyExist = data.hierarchy_exists;
            this.isHierarchyLevelExist = data.hierarchy_level_exists;
            this.hierarchyCount = data.hierarchy_count;
            this.hierarchyLevelCount = data.hierarchy_level_count;
            this.buttonManageHierarchy.caption = this.isHierarchyExist ? `View Hierarchy Levels (${this.hierarchyLevelCount})` : "Manage Hierarchy Levels";
            this.buttonManageHierarchy.tooltip = this.isHierarchyExist ?
                "A hierarchy has already been created. You can only view the existing Hierarchy Levels." :
                "Click to manage Hierarchy Levels and define the Hierarchy Levels.";
            this.layoutManageHierarchyLevel.tableHierarchyLevel.displayToolsCell = this.isHierarchyExist ? false : true;
        }
    }

    buttonManageHierarchyOnClick(event: ClickEvent) {
        const layout = this.layoutManageHierarchyLevel;
        const callbackFunction = async () => {
            await this.refreshHierarchyStatus();
            await this.populateTree();
        };
        layout.showHierarchyLevelSlideout(this.isHierarchyExist, true, callbackFunction);
    }

    async populateTree() {
        const { hierarchyMap, highestHierarchyLevel, lowestHierarchyLevel } = await ResponsibilityUtil.getHierarchyLevelInfo();
        const hierarchyData = await this.fetchHierarchyData();
        const treeMenu = this.treeMenu;
        const root = treeMenu.getRootNode();

        // Setup tree UI
        treeMenu.selectedNodeBackgroundColor = "#e0f0ff";
        treeMenu.selectedNodeColor = (node) => node.color;
        root.removeAllChildren();

        const nodeMap: Record<string, TreeNode> = {};
        const childrenMap: Record<string, string[]> = {};

        for (const item of hierarchyData) {
            const node = this.createTreeNode(item, hierarchyMap);
            nodeMap[item.id] = node;

            if (item.parent_id) {
                if (!childrenMap[item.parent_id]) {
                    childrenMap[item.parent_id] = [];
                }
                childrenMap[item.parent_id].push(item.id);
            }
        }

        const topNodes = this.buildHierarchyStructure(hierarchyData, nodeMap);
        this.addPlaceholders(hierarchyData, nodeMap, childrenMap, lowestHierarchyLevel);

        const headerNode = this.createHeaderNode(topNodes, highestHierarchyLevel, hierarchyMap);

        root.addChild(headerNode);
        // If only headerNode needs to be expanded. >> headerNode.expanded = true;
        root.expandAll();
        treeMenu.selectedNode = null;
    }

    private async fetchHierarchyData(): Promise<HierarchyItem[]> {
        const response = await Api.search("lme/general/hierarchy-tree");
        return response?.data?.length ? response.data : [];
    }

    private createTreeNode(hierarchyItem: HierarchyItem, hierarchyMap: Map<number, string>): TreeNode {
        const description = hierarchyMap.get(hierarchyItem.hierarchy_level);
            const isOverLimit = hierarchyItem.description.length > ResponsibilityUtil.getHierarchyDescriptionLength();
            const nodeTextDescription = isOverLimit
                ? hierarchyItem.description.substring(0, 50) + " ... View more"
                : hierarchyItem.description;
              const nodeText = `${description}: ${hierarchyItem.id}: ${nodeTextDescription}`;

        const node = new TreeNode({
            text: nodeText,
            expandedImageName: ImageName.checkboxIndeterminate,
            collapsedImageName: ImageName.addInBox,
            padding: 0
        });

        if (isOverLimit) {
            const tooltipLabel = new Label({ padding: 0,  margin: 0, rowBreak: false, fillRow: true, scrollX: true, maxWidth: 500, maxHeight: 300});
            tooltipLabel.text = hierarchyItem.description;
            node.component.tooltip = tooltipLabel;
        }

        node.expandOnClick = false;
        node.setLabelProps({
            imageProps: {
                onClick: (evt: ClickEvent) => {
                    node.expanded = !node.expanded;
                    evt.stopPropagation();
                }
            }
        });

        const rightPanel = new Panel({ padding: 0, margin: 0, rowBreak: false });
        const childLevel = hierarchyItem.hierarchy_level - 1;
        const canAdd = childLevel > 0;

        const addBtn = this.createTreeButton(canAdd ? 'Add Hierarchy' : 'Cannot Add to Responsibility Codes', ImageName.add, canAdd);
        const updateBtn = this.createTreeButton('Update Hierarchy', ImageName.pencil, true);
        const deleteBtn = this.createTreeButton('Delete Hierarchy', ImageName.delete, true);

        addBtn.addClickListener((event: ClickEvent) => this.nodeAddIconOnClick(event, hierarchyItem, hierarchyMap));
        updateBtn.addClickListener((event: ClickEvent) => this.nodeUpdateIconOnClick(event, hierarchyItem, hierarchyMap));
        deleteBtn.addClickListener((event: ClickEvent) => this.nodeIconDeleteOnClick(event, node, hierarchyItem));

        rightPanel.add(addBtn, updateBtn, deleteBtn);

        node.rightComponent = rightPanel;
        node.onlyShowRightComponentWhenHovered = true;

        this.applyTreeNodeHoverBehavior(node);

        return node;
    }

    private buildHierarchyStructure(hierarchyData: HierarchyItem[], nodeMap: Record<string, TreeNode>): TreeNode[] {
        const topNodes: TreeNode[] = [];

        for (const item of hierarchyData) {
            const node = nodeMap[item.id];
            if (!item.parent_id) {
                topNodes.push(node);
            } else {
                const parent = nodeMap[item.parent_id];
                if (parent) {
                    parent.addChild(node);
                }
            }
        }

        return topNodes;
    }

    private addPlaceholders( hierarchyData: HierarchyItem[], nodeMap: Record<string, TreeNode>, childrenMap: Record<string, string[]>, lowestLevel: number) {
        for (const item of hierarchyData) {
            const node = nodeMap[item.id];
            const hasChildren = childrenMap[item.id]?.length > 0;
            if (!hasChildren && item.hierarchy_level !== lowestLevel) {
                node.addChild(this.createPlaceHolderNode());
            }
        }
    }

    private createHeaderNode(topNodes: TreeNode[], highestLevel: number, hierarchyMap: Map<number, string>): TreeNode {
        const headerNode = new TreeNode({
            text: ResponsibilityUtil.getCompanyName(),
            selectable: true,
            expanded: true,
            expandedImageName: ImageName.checkboxIndeterminate,
            collapsedImageName: ImageName.addInBox
        });

        headerNode.expandOnClick = false;
        headerNode.setLabelProps({
            imageProps: {
                onClick: (evt: ClickEvent) => {
                    headerNode.expanded = !headerNode.expanded;
                    evt.stopPropagation();
                }
            }
        });

        const panel = new Panel({ padding: 0, margin: 0, rowBreak: false });
        const canAdd = this.isHierarchyLevelExist;
        const addTooltip = canAdd ? "Add Hierarchy" : "Cannot Add Hierarchy until Hierarchy Level is defined";

        const addBtn = this.createTreeButton(addTooltip, ImageName.add, canAdd);
        const updateBtn = this.createTreeButton('Cannot Update Hierarchy at top-level item', ImageName.noPencil, false);
        const deleteBtn = this.createTreeButton('Cannot Delete Hierarchy at top-level item', ImageName.delete, false);

        addBtn.addClickListener((evt: ClickEvent) => this.nodeHeaderAddIconOnClick(evt, highestLevel, hierarchyMap));

        panel.add(addBtn, updateBtn, deleteBtn);
        headerNode.rightComponent = panel;
        headerNode.onlyShowRightComponentWhenHovered = false;

        this.applyTreeNodeHoverBehavior(headerNode);

        if (topNodes.length === 0) {
            headerNode.addChild(this.createPlaceHolderNode());
        } else {
            for (const node of topNodes) {
                headerNode.addChild(node);
            }
        }
        return headerNode;
    }

    private applyTreeNodeHoverBehavior(node: TreeNode) {
        const nodeElement = node.getTreeNodeComponent();
        nodeElement.onmouseenter = () => {
            nodeElement.style.backgroundColor = "#e0f0ff";
        };

        nodeElement.onmouseleave = () => {
            nodeElement.style.backgroundColor = "";
        };
    }

    createTreeButton(tooltip: string, imageName: ImageName, shouldEnabled: boolean): Button {
        const button = new Button({ tooltip: tooltip, imageName: imageName, rowBreak: false, size: "small", borderWidth: 0, padding: 4});
        button.enabled = shouldEnabled;
        return button;
    }

    createPlaceHolderNode(): TreeNode {
        const placeholderNode = new TreeNode({
            text: ResponsibilityUtil.getPlaceHolderText(), color: "default.lightest", selectable: false,
            imageName: ImageName.info, imageColor: "primary", expandedImageName: null, collapsedImageName: null, expanded: false
        });
        return placeholderNode;
    }

    private nodeAddIconOnClick(event: ClickEvent, hierarchyItem: HierarchyItem, hierarchyMap: Map<number, string>) {
        event.stopPropagation();
        const layout = this.layoutManageHierarchy;
        const level: number = hierarchyItem.hierarchy_level - 1;
        const description: string = hierarchyMap.get(level);
        const parentId: string | undefined = hierarchyItem.id;
        layout.showAddHierarchy(level, parentId, description, true, async () => {
            await this.refreshHierarchyStatus();
            await this.populateTree();
        });
    }

    private nodeHeaderAddIconOnClick(event: ClickEvent, highestHierarchyLevel: number, hierarchyMap: Map<number, string>) {
        event.stopPropagation();
        const layout = this.layoutManageHierarchy;
        const level: number = highestHierarchyLevel;
        const description: string = hierarchyMap.get(level);
        if (level === 0) {
            CommonDialogs.showError("You cannot add to responsibility Codes");
            return;
        }
        layout.showAddHierarchy(level, null, description, true, async () => {
            await this.refreshHierarchyStatus();
            if (this.hierarchyCount === 1) {
                await Navigation.reloadCurrentPage(true);
            }
            await this.populateTree();
        });
    }

    private nodeUpdateIconOnClick(event: ClickEvent, item: HierarchyItem, hierarchyMap: Map<number, string>) {
        event.stopPropagation();
        const level: number = item.hierarchy_level;
        const description: string = hierarchyMap.get(level);
        const hierarchyRow: ModelRow = new ModelRow("lme/general/hierarchy", false, item);
        const layout = this.layoutManageHierarchy;
        layout.showUpdateHierarchy(level, hierarchyRow, description, true, async () => {
            await this.populateTree();
        });
    }

    private async nodeIconDeleteOnClick(event: ClickEvent, node: TreeNode, item: any) {
        event.stopPropagation();
        const layout = this.layoutManageHierarchy;
        try {
            await layout.deleteHierarchy(node, item, true, async () => {
                await this.refreshHierarchyStatus();
                if (!this.isHierarchyExist) {
                    await Navigation.reloadCurrentPage(true);
                }
                await this.populateTree();
            });
        } catch (ex) {
            CommonDialogs.showError(ex);
        }
    }

}
